import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/lib/permissions';
import { useBoards } from '@/hooks/use-boards';
import { useTasks } from '@/hooks/use-tasks';
import { useAllIssues } from '@/hooks/use-all-issues';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useAuthStore } from '@/stores/auth';
import { useExtensionsStore } from '@/stores/extensions';
import { cn } from '@/lib/utils';
import { formatDate, isPastDateOnly } from '@/lib/date';
import type { BoardListItem, TaskType } from '@hygge/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Clock, Flame, Layers, Plus, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AiCommandBar } from './ai-command-bar';
import { CreateBoardDialog } from './create-board-dialog';
import { BoardsGrid } from './boards-grid';
import { WorkspaceSummary } from './workspace-summary';
import { TYPE_CONFIG } from './task-config';
import { DUE_SOON_SCAN_LIMIT, selectDueSoon } from '../lib/due-soon';

// ─── Muted professional palette ─────────────────────────────────────────
const BOARD_PALETTE = [
  '#5d6d7e', '#6b8e7b', '#7b6d8e', '#8e7b5d',
  '#7b5d6d', '#5d7e7b', '#7e5d5d', '#8e6d5d',
];

const RECENT_BOARDS_KEY = 'tasks-recent-boards';
const MAX_RECENT = 6;

// Boards AI ask bar is temporarily hidden; kept in code for a future AI feature.
// Flip to `true` to re-enable the ask bar, command bar, per-row Ask AI, and ⌘K.
// It is ANDed with the `ai-assistant` extension below, so re-enabling it here
// still leaves the extension as the switch that governs.
const BOARDS_AI_ENABLED = false;

function getRecentBoardIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_BOARDS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function trackBoardVisit(boardId: string) {
  const ids = getRecentBoardIds().filter((id) => id !== boardId);
  ids.unshift(boardId);
  localStorage.setItem(RECENT_BOARDS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
}

function getBoardColor(board: BoardListItem, index: number): string {
  if (board.color && BOARD_PALETTE.includes(board.color)) return board.color;
  return BOARD_PALETTE[index % BOARD_PALETTE.length];
}


// ─── Main Dashboard ─────────────────────────────────────────────────────
interface TasksDashboardProps {
  onBoardSelect: (boardId: string) => void;
}

export function TasksDashboard({ onBoardSelect }: TasksDashboardProps) {
  const canCreateBoard = usePermission('boards.create');
  const navigate = useNavigate();
  const { data: boardsData, isLoading } = useBoards();
  const boards = boardsData ?? [];
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [aiBoard, setAiBoard] = useState<{ id: string; name: string } | null>(null);
  const user = useAuthStore((s) => s.user);
  const employeeId = user?.employee?.id;
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));
  // Both switches have to agree: the local flag says whether this surface is
  // finished, the extension says whether AI is on at all.
  const showAi = BOARDS_AI_ENABLED && isAssistantEnabled;

  // ─── Search ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 200);
  const isSearching = debouncedSearch.length > 0;

  const projectBoards = boards.filter((b: BoardListItem) => b.projectId);
  const standaloneBoards = boards.filter((b: BoardListItem) => !b.projectId);
  const allBoards = useMemo(
    () => [...projectBoards, ...standaloneBoards],
    [projectBoards, standaloneBoards],
  );




  // ⌘K shortcut to open AI command bar
  useEffect(() => {
    if (!showAi) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsAiOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showAi]);



  // ─── Recently visited ────────────────────────────────────────────────

  const handleBoardCreated = useCallback((newBoardId: string) => {
    navigate({ to: '/boards', search: { board: newBoardId } });
  }, [navigate]);

  if (isLoading) return <DashboardSkeleton />;

  return (
    <>
      <div className="flex-1 overflow-auto">
        <div className="@container mx-auto w-full max-w-[1200px] px-6 py-8 @2xl:px-8">
          {/* Title + summary line */}
          <div className="mb-7 flex items-start justify-between gap-4">
            <div>
              {/* The headline lives in WorkspaceSummary now, which opens with
                  what the workspace is doing rather than how many rows it
                  holds. The old subtitle summed `taskCounter` — the counter the
                  next key is minted from, not a count of tasks. */}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate({ to: '/boards', search: { view: 'all-issues' } })}
                className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3.5 text-[13px] font-medium text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
              >
                <Layers className="h-4 w-4" />
                All issues
              </button>
              {canCreateBoard && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setIsCreatingBoard(true)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-hygge/30 bg-hygge/10 text-hygge shadow-sm transition-colors hover:bg-hygge/20 cursor-pointer"
                      aria-label="New board"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>New board</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              )}
            </div>
          </div>

          {/* AI Ask Bar */}
          {showAi && (
          <div className="relative mb-8">
            <div className="relative group/ask">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center pointer-events-none shadow-sm shadow-violet-500/30">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <input
                type="text"
                className="w-full rounded-xl border border-border bg-card pl-14 pr-16 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-all focus:border-primary/40 focus:ring-[3px] focus:ring-primary/8 focus:shadow-sm cursor-pointer"
                placeholder="Ask about your projects, find tasks, or jump to a board..."
                onClick={() => setIsAiOpen(true)}
                readOnly
              />
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                <kbd className="font-mono text-[10px] font-medium text-muted-foreground/70 bg-muted/60 px-1.5 py-0.5 rounded border border-border/60">
                  &#8984;K
                </kbd>
              </div>
            </div>
            <div className="flex gap-1.5 mt-2.5 pl-0.5">
              {[
                { label: 'What\u2019s overdue?', icon: '!' },
                { label: 'Sprint summary', icon: '\u21BB' },
                { label: 'Unassigned tasks', icon: '?' },
                { label: 'Blocked items', icon: '\u26A0' },
              ].map((hint) => (
                <button
                  key={hint.label}
                  onClick={() => setIsAiOpen(true)}
                  className="text-[11px] text-muted-foreground/70 pl-2 pr-2.5 py-1 rounded-lg bg-transparent hover:bg-muted hover:text-foreground transition-colors border border-border/50 hover:border-border cursor-pointer flex items-center gap-1.5"
                >
                  <span className="text-[10px] opacity-50">{hint.icon}</span>
                  {hint.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {showAi && (
          <AiCommandBar
            open={isAiOpen}
            onOpenChange={(o) => {
              setIsAiOpen(o);
              if (!o) setAiBoard(null);
            }}
            boardId={aiBoard?.id}
            boardName={aiBoard?.name}
            userInitials={user?.employee ? `${user.employee.firstName?.[0] ?? ''}${user.employee.lastName?.[0] ?? ''}` : undefined}
          />
          )}

          {/* The summary: what the workspace is doing, in a sentence, then the
              running sprints ranked by whether they will land. */}
          {!isSearching && <WorkspaceSummary boards={allBoards} />}

          <BoardsGrid
            boards={allBoards}
            search={searchQuery}
            onSearchChange={setSearchQuery}
            onBoardSelect={onBoardSelect}
            onCreate={() => setIsCreatingBoard(true)}
          />

          {/* ─── Two-column: My tasks + Due soon ─────────────────────── */}
          {!isSearching && employeeId && allBoards.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-8">
              <MyTasksSection employeeId={employeeId} />
              <DueSoonSection boards={allBoards} />
            </div>
          )}
        </div>
      </div>

      <CreateBoardDialog
        open={isCreatingBoard}
        onOpenChange={setIsCreatingBoard}
        onCreated={handleBoardCreated}
      />
    </>
  );
}


// ─── Due Soon Section ───────────────────────────────────────────────────
function DueSoonSection({ boards }: { boards: BoardListItem[] }) {
  const firstBoards = boards.slice(0, 5);

  // Collect due tasks from all boards in one pass
  return (
    <section>
      <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1.5">
        <Flame className="h-3.5 w-3.5" />
        Due soon
      </h2>
      <div className="rounded-[10px] border border-border overflow-hidden">
        <DueSoonList boards={firstBoards} />
      </div>
    </section>
  );
}

function DueSoonList({ boards }: { boards: BoardListItem[] }) {
  const navigate = useNavigate();

  // Load tasks from each board. The list filters client-side, so the size of
  // the page it asks for decides what a reader can see — see `due-soon.ts`.
  const b0 = useTasks(boards[0]?.id, { limit: DUE_SOON_SCAN_LIMIT });
  const b1 = useTasks(boards[1]?.id, { limit: DUE_SOON_SCAN_LIMIT });
  const b2 = useTasks(boards[2]?.id, { limit: DUE_SOON_SCAN_LIMIT });
  const b3 = useTasks(boards[3]?.id, { limit: DUE_SOON_SCAN_LIMIT });
  const b4 = useTasks(boards[4]?.id, { limit: DUE_SOON_SCAN_LIMIT });

  const dueTasks = useMemo(() => {
    const queries = [b0, b1, b2, b3, b4];
    return selectDueSoon(
      boards.map((board, i) => ({ board, tasks: queries[i]?.data?.data ?? [] })),
    );
  }, [boards, b0.data, b1.data, b2.data, b3.data, b4.data]);

  if (dueTasks.length === 0) {
    return (
      <div className="px-4 py-5 text-center text-[12px] text-muted-foreground">
        <Clock className="h-4 w-4 mx-auto mb-1 opacity-50" />
        Nothing due in the next 7 days
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {dueTasks.map(({ task, board, boardIndex }) => {
        const color = getBoardColor(board, boardIndex);
        const isOverdue = isPastDateOnly(task.dueDate);
        return (
          <button
            key={task.id}
            onClick={() => navigate({ to: '/boards', search: { board: board.id, task: task.key } })}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 bg-card hover:bg-muted/50 transition-colors cursor-pointer text-left"
          >
            <span className={`font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${isOverdue ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'}`}>
              {formatDate(task.dueDate, 'monthDay')}
            </span>
            <span className="text-[12px] flex-1 min-w-0 truncate">{task.title}</span>
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
              style={{ background: `${color}12`, color }}
            >
              {board.slug}
            </span>
          </button>
        );
      })}
    </div>
  );
}



// ─── My Tasks Section ───────────────────────────────────────────────────
/** How many rows this section shows before it says how many it is holding back. */
const MY_TASKS_VISIBLE = 10;

/**
 * The reader's own work, in due order.
 *
 * It used to ask the first five boards for three tasks each: a task due
 * tomorrow on the sixth board was invisible, the fifteen rows arrived in
 * whatever order the boards came back in, and something due in September sat
 * above something overdue in July. Ordering a list of work by anything other
 * than when it is due is the same as not ordering it.
 *
 * One pooled request now, across every board the reader can see, sorted by due
 * date. Ten rows, and the count of what is not shown — the list is capped, so
 * it has to say so.
 */
function MyTasksSection({ employeeId }: { employeeId: string }) {
  const navigate = useNavigate();
  const { data, isPending } = useAllIssues(
    { assigneeId: employeeId, sort: 'dueDate', order: 'asc' },
    { enabled: !!employeeId },
  );

  const open = (data?.data ?? []).filter(
    (t) => t.status.category !== 'done' && t.status.category !== 'cancelled',
  );
  const shown = open.slice(0, MY_TASKS_VISIBLE);
  const hidden = open.length - shown.length;

  return (
    <section>
      <div className="mb-2.5 flex items-baseline justify-between gap-4">
        <h2 className="text-[13px] font-semibold text-foreground">Assigned to me</h2>
        {!isPending && (
          <span className="text-[11px] tabular-nums text-muted-foreground">{open.length} open</span>
        )}
      </div>

      {isPending ? (
        <div className="space-y-px">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="flex min-h-8 items-center text-xs text-muted-foreground">
          Nothing is assigned to you.
        </p>
      ) : (
        <div>
          {shown.map((task) => (
            <button
              key={task.id}
              onClick={() =>
                navigate({ to: '/boards', search: { board: task.board.id, task: task.key } })
              }
              className="-mx-3 flex min-h-8 w-full cursor-pointer items-center gap-2.5 rounded-md px-3 py-1.5 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <TaskTypeIcon type={task.type} />
              <span className="w-16 shrink-0 font-mono text-[10px] text-muted-foreground/70">
                {task.key}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-foreground">
                {task.title}
              </span>
              <span
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{ background: task.board.color || 'hsl(var(--muted))' }}
                title={task.board.name}
              />
              <span
                className={cn(
                  'w-14 shrink-0 text-right text-[11px] tabular-nums',
                  isPastDateOnly(task.dueDate) ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {task.dueDate ? formatDate(task.dueDate, 'monthDay') : '—'}
              </span>
            </button>
          ))}

          {hidden > 0 && (
            <button
              type="button"
              onClick={() => navigate({ to: '/boards', search: { view: 'all-issues' } })}
              className="-mx-3 mt-1 flex w-full cursor-pointer items-center rounded-md px-3 py-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {hidden} more assigned to you →
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Task Type Icon ─────────────────────────────────────────────────────
function TaskTypeIcon({ type }: { type: TaskType }) {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.task;
  const Icon = cfg.icon;
  return <Icon className={`h-4 w-4 shrink-0 ${cfg.color}`} />;
}

// ─── Skeleton ───────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="flex-1 overflow-auto">
      <div className="@container mx-auto w-full max-w-[1200px] px-6 py-8 @2xl:px-8">
        <Skeleton className="h-6 w-24 mb-1" />
        <Skeleton className="h-3 w-48 mb-7" />
        <Skeleton className="h-12 w-full rounded-xl mb-8" />
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="space-y-2">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-20 rounded-[10px]" />
            <Skeleton className="h-20 rounded-[10px]" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-20 rounded-[10px]" />
          </div>
        </div>
        <Skeleton className="h-3 w-20 mb-3" />
        <div className="rounded-[10px] border border-border overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-none" />
          ))}
        </div>
      </div>
    </div>
  );
}
